/**
 * sandboxSphere.ts
 *
 * The composer's *Create as sandbox sphere* action (EAINT-897 Phase 5): a real
 * sphere of the type under composition, stood up so the author can validate the
 * type by example and then throw it away.
 *
 * Two things about the request body are load-bearing:
 *   - `is_private: true` — the sandbox is visible only to its creator, so a
 *     half-formed type never leaks into anyone else's graph.
 *   - `draft: false` — NOT a draft capture. A draft skips arc deployment, which
 *     is precisely what the author is here to see; a sandbox sphere must behave
 *     like a sphere someone actually created.
 *
 * `isSystemOperation` is never sent: the server derives it from an authorized
 * service identity, and a user-initiated composer action is not one.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import { createSphere } from './marketplaceApi';

export interface CreateSandboxSphereParams {
  /** The type being composed — the sandbox sphere is an instance of it. */
  schemaSystemName: string;
  /** The sphere the sandbox is centered on: the composing organization. */
  centerSphereId: string;
  name: string;
}

export type SandboxSphereOutcome =
  | { outcome: 'created'; sphereId: string }
  /** The caller may not create spheres here (403) — the button states the reason. */
  | { outcome: 'refused' };

export type DeleteSandboxSphereOutcome = { outcome: 'deleted' } | { outcome: 'refused' };

function statusOf(err: unknown): number | undefined {
  const anyErr = err as { response?: { status?: number }; status?: number } | undefined;
  return anyErr?.response?.status ?? anyErr?.status;
}

export async function createSandboxSphere(
  graphClient: ApiClient,
  { schemaSystemName, centerSphereId, name }: CreateSandboxSphereParams
): Promise<SandboxSphereOutcome> {
  try {
    const response = await createSphere(graphClient, {
      schemaSystemName,
      draft: false,
      sphere: {
        properties: { name, is_private: true },
        assignedPerspectives: [
          {
            perspectiveProperties: { system_name: 'center_center' },
            assigned: [{ sphere: { id: centerSphereId } }],
          },
        ],
      },
    });
    const sphereId = response.sphereId ?? response.sphere?.id;
    if (!sphereId) {
      throw new Error('createSandboxSphere: the server returned no sphere id');
    }
    return { outcome: 'created', sphereId: String(sphereId) };
  } catch (err) {
    if (statusOf(err) === 403) return { outcome: 'refused' };
    throw err;
  }
}

export async function deleteSandboxSphere(
  graphClient: ApiClient,
  sphereId: string
): Promise<DeleteSandboxSphereOutcome> {
  try {
    await graphClient.delete(`/spheres/${encodeURIComponent(sphereId)}/delete`);
    return { outcome: 'deleted' };
  } catch (err) {
    if (statusOf(err) === 403) return { outcome: 'refused' };
    throw err;
  }
}
